import { useState } from "react";

export interface GpsFix {
  latitude: number;
  longitude: number;
  /** Raio de incerteza informado pelo aparelho, em metros. */
  accuracy: number;
}

interface GpsLocatorProps {
  onFix: (fix: GpsFix) => void;
  /** Leitura mais recente aplicada ao marcador, para exibir a precisão. */
  accuracy: number | null;
}

const GOOD_ACCURACY_M = 5;
const ACCEPTABLE_ACCURACY_M = 10;

const ERROR_TEXT: Record<number, string> = {
  1: "Permissão de localização negada. Libere o acesso à localização para este site nas configurações do navegador.",
  2: "Não foi possível obter a posição. Verifique se o GPS do aparelho está ligado.",
  3: "O GPS demorou demais para responder. Vá para um local aberto e tente de novo.",
};

/**
 * Captura a posição pelo GPS do aparelho. A precisão é mostrada e nunca
 * escondida ou arredondada para parecer melhor: o manual do CTA pede que a
 * incerteza declarada seja a que o aparelho informou, sem inventar precisão.
 */
export default function GpsLocator({ onFix, accuracy }: GpsLocatorProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function capture() {
    if (!navigator.geolocation) {
      setError("Este navegador não oferece localização por GPS.");
      return;
    }
    setLoading(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLoading(false);
        onFix({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (err) => {
        setLoading(false);
        setError(ERROR_TEXT[err.code] ?? "Falha ao obter a localização.");
      },
      // maximumAge 0: uma posição guardada de outro lugar seria gravada como se fosse daqui.
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  }

  const quality =
    accuracy == null
      ? null
      : accuracy <= GOOD_ACCURACY_M
        ? { text: "boa", cls: "text-primary-700 bg-primary-50 border-primary-200" }
        : accuracy <= ACCEPTABLE_ACCURACY_M
          ? { text: "aceitável", cls: "text-amber-800 bg-amber-50 border-amber-200" }
          : { text: "baixa", cls: "text-red-700 bg-red-50 border-red-200" };

  return (
    <div className="space-y-2">
      <button
        onClick={capture}
        disabled={loading}
        className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium cursor-pointer hover:bg-primary-700 disabled:bg-background-300 disabled:cursor-wait"
      >
        <i className={loading ? "ri-loader-4-line animate-spin" : "ri-gps-line"} />
        {loading ? "Obtendo posição..." : accuracy == null ? "Usar minha localização" : "Capturar novamente"}
      </button>

      {quality && accuracy != null && (
        <div className={`rounded-lg border px-3 py-2 text-xs ${quality.cls}`}>
          Precisão do GPS: <strong>{accuracy.toFixed(1)} m</strong> ({quality.text}).
          {accuracy > ACCEPTABLE_ACCURACY_M &&
            " Afaste-se de edificações e de copas densas e capture de novo antes de continuar."}
          {accuracy <= ACCEPTABLE_ACCURACY_M &&
            " Confira o marcador no mapa: ele deve estar na base do tronco."}
        </div>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
